<template>

  <v-container fluid class="pa-0">

    <v-form ref="readKeyForm" autocomplete="off" :disabled="componentDisabled" @submit.prevent="submit">
      <v-layout align-center>

        <v-flex :class="{'ie-flex-fix': isIE}">
          <app-text-field :value="keyFileName" readonly label="appSignFilePrivateKeyReader.filekey" :clearable="!componentDisabled" required
            class="app-sign-text-field" :validate-on-blur="false" @click:clear="clearKeyFile">
          </app-text-field>

        </v-flex>

        <v-btn :id="fileKeySelectBtnId" :disabled="componentDisabled" color="primary" class="ml-2 mt-1" @click="openKeyFileDlg">
          {{$t('appSignFilePrivateKeyReader.selectkey')}}
        </v-btn>

        <input ref="fileSign" type="file" hidden @change="onFileChange">

      </v-layout>

      <v-layout v-show="isJks" align-center>

        <app-select v-model="selectJks" :items="jksKeys" return-object item-text="alias" :class="{'ie-flex-fix': isIE}"
          label="appSignFilePrivateKeyReader.keyjsk" :disabled="componentDisabled">
        </app-select>

      </v-layout>

      <v-layout align-center>

        <app-password-field ref="password" v-model="keyPassword" :disabled="componentDisabled" label="appSignFilePrivateKeyReader.password"
          autocomplete="on" name="file-signature-password" :class="{'ie-flex-fix': isIE}" required :minPasswordLen="1"
          class="app-sign-text-field" :validate-on-blur="false">
        </app-password-field>

        <v-btn v-if="keyIsReaded" color="warning" class="ml-2 mt-1" @click="onResetBtnClicked">
          {{$t('buttons.removesign')}}
        </v-btn>
        <v-btn v-else :id="fileKeyReadBtnId" type="submit" :disabled="componentDisabled" color="primary" class="ml-2 mt-1">
          {{$t('appSignFilePrivateKeyReader.read')}}
        </v-btn>

      </v-layout>

    </v-form>

    <div v-if="messageText" v-html="messageText" :class="[ 'info-panel pa-2', messageIsError ? 'error': 'success' ]">
    </div>

    <div v-if="warningText" v-html="warningText" class="info-panel pa-2 warning mt-1">
    </div>

  </v-container>

</template>

<script>
import AppPasswordField from '@/components/Common/AppPasswordField';
import AppSelect from '@/components/Common/AppSelect';
import AppTextField from '@/components/Common/AppTextField';
import AppSignBasePrivateKeyReader from './AppSignBasePrivateKeyReader';

export default {
  name: 'AppSignFilePrivateKeyReader',
  extends: AppSignBasePrivateKeyReader,
  components: { AppPasswordField, AppSelect, AppTextField },
  inject: ['isMobileOnly', 'isIE'],
  data() {
    return {
      jksKeys: [],
      isJks: false,
      keyFile: Uint8Array | null,
      keyFileName: '',
      selectJks: null,
      useCtxEuSignFunctions: true
    };
  },
  computed: {
    fileKeySelectBtnId() {
      return `appSign${this.privateKeyType}FileSelectBtn`;
    },
    fileKeyReadBtnId() {
      return `appSign${this.privateKeyType}FileReadBtn`;
    },
    signingParamsRequestMsg() {
      return `${this.signingParamsRequestMsgBase}-FileSelector`;
    },
    signingParamsResponseMsg() {
      return `${this.signingParamsResponseMsgBase}-FileSelector`;
    }
  },
  methods: {
    clearKeyFile() {
      this.keyFile = null;
      this.keyFileName = '';
      this.selectJks = null;
      this.isJks = false;
      this.$refs.fileSign.value = '';
    },
    async loadJksKeys() {
      let loader = this.showLoading();
      try {
        this.jksKeys = await this.euSignLibrary.GetJKSPrivateKeys(this.keyFile);
        if (this.jksKeys && this.jksKeys.length) {
          this.selectJks = this.jksKeys[0];
        }
      } finally {
        loader.hide();
      }
    },
    onFileChange(e) {
      const files = e.target.files || e.dataTransfer.files;

      if (files.length > 0) {
        const reader = new FileReader();
        reader.onload = async(event) => {
          this.keyFile = new Uint8Array(event.target.result);
          this.keyFileName = files[0].name;

          const ext = this.keyFileName.split('.');
          this.isJks = ext && ext.length > 1 && ext[1].toLowerCase() === 'jks';
          if (this.isJks) {
            await this.loadJksKeys();
          } else {
            this.jksKeys = [];
          }
          this.setFocusToPswControl();
        };
        reader.readAsArrayBuffer(files[0]);
      }
    },
    openKeyFileDlg() {
      this.clearKeyFile();
      this.$refs.fileSign.click();
    },
    readKey(certs) {
      return this.euSignLibrary.CtxReadPrivateKeyBinary(
        this.euSignContext,
        this.isJks ? this.selectJks.privateKey : this.keyFile,
        this.keyPassword,
        certs,
        this.certificateAuthority ? this.certificateAuthority.issuerCNs[0] : undefined
      );
    },
    async reset() {
      this.clearKeyFile();
      this.jksKeys = [];
      await this.resetBase();
    },
    async submit() {
      if (this.$refs.readKeyForm.validate()) {
        await this.readKeyClick();
      }
    },
    setFocusToPswControl() {
      const inputs = this.$refs.password.$el.children[1].getElementsByTagName('input');
      if (inputs && inputs.length) {
        inputs[0].focus();
      }
    },
    async tryToReadKey() {
      await this.submit();
    },
    onPrepareComponent() {
      this.$refs.readKeyForm.resetValidation();
    }
  }
};
</script>

<style lang="scss">
.ie-flex-fix {
  flex: 1;
}
</style>
